import { ChangeDetectorRef, Component, output, ViewChild } from '@angular/core';
import { CdkDragDrop, CdkDropList, CdkDrag, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatTable, MatTableModule } from '@angular/material/table';
import { Review } from '../../../../../../state-management/model/interfaces/state.model';
import { WebsiteStateService } from '../../../../../../state-management/services/website-state.service';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ReviewMode } from '../website-config-reviews.component';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { EllipsisDirective } from 'ngx-ellipsis';


export interface ReviewExtended extends Review {
  expanded: boolean;
  isTruncated: boolean;
}

@Component({
  selector: 'app-website-config-reviews-order',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule, 
    CdkDropList, 
    CdkDrag, 
    MatIconModule, 
    MatButtonModule,
    MatIconModule,
    MatButtonToggleModule, 
    MatDividerModule, 
    EllipsisDirective
  ],
  templateUrl: './website-config-reviews-order.component.html',
  styleUrl: './website-config-reviews-order.component.scss'
})
export class WebsiteConfigReviewsOrderComponent {
  @ViewChild('table', {static: false}) table!: MatTable<ReviewExtended>;
  @ViewChild(EllipsisDirective) ellipsisRef!: EllipsisDirective;
  readonly reviewModeChange = output<ReviewMode>();
  readonly ReviewMode = ReviewMode;
  public reviewData: Review[] = this.websiteState.configuration().reviews;
  public ratingOptions = [1, 2, 3, 4, 5];
  public selectedMode: ReviewMode = ReviewMode.Order;
  public displayedColumns: string[] = ['actions', 'name', 'rating', 'text'];
  public dataSource : ReviewExtended[] = this.reviewData
  .filter(review => review.show)
  .map(review => ({ ...review, expanded: false, isTruncated: false }))
  .sort((a, b) => {
    if (a.websitePosition === null) return 1; 
    if (b.websitePosition === null) return -1; 
    return a.websitePosition - b.websitePosition;
  });
  
  constructor(private websiteState: WebsiteStateService, private cd: ChangeDetectorRef) { }

  public drop(event: CdkDragDrop<string>): void {
    const previousIndex = this.dataSource.findIndex(d => d === event.item.data);
    moveItemInArray(this.dataSource, previousIndex, event.currentIndex);
    this.table.renderRows();
    // Aktualisiere WebsitePosition für jedes Element
    this.dataSource.forEach((review, index) => {
      review.websitePosition = index + 1; // Index beginnt bei 0, Position beginnt bei 1
    });   
    this.updateReviewOrder();
  }

  public moveUp(index: number): void {
    if (index > 0) {
      moveItemInArray(this.dataSource, index, index - 1);
      this.table.renderRows();
      this.updateWebsitePositions();
      this.updateReviewOrder();
    }
  }
  
  public moveDown(index: number): void {
    if (index < this.dataSource.length - 1) {
      moveItemInArray(this.dataSource, index, index + 1);
      this.table.renderRows();
      this.updateWebsitePositions();
      this.updateReviewOrder();
    }
  }

  private updateWebsitePositions(): void {
    this.dataSource.forEach((review, index) => {
      review.websitePosition = index + 1; // Index beginnt bei 0, Position beginnt bei 1
    });
  }

  private updateReviewOrder() {
    const updatedReviews = this.reviewData.map(review => {
      const updatedReview = this.dataSource.find(dsReview => dsReview.id === review.id);
      if (updatedReview) {
        const { expanded, ...rest } = updatedReview;
        return rest;
      }
      return review;
    }).sort((a, b) => {
      // Sortiere nach `websitePosition`
      if (a.websitePosition === null) return 1;
      if (b.websitePosition === null) return -1;
      return a.websitePosition - b.websitePosition;
    });
    this.websiteState.updateConfiguration({
      reviews: updatedReviews,
    });
  }

  public onReviewModeChange(value: ReviewMode): void {
    this.selectedMode = value;
    this.reviewModeChange.emit(this.selectedMode);
  }

  public onTextTruncated(truncated: number, review: ReviewExtended): void {
    review.isTruncated = truncated > 0;
  }
  
  public showComplete(review: ReviewExtended): void {
    review.expanded = true;
    this.cd.detectChanges();
    this.applyEllipsis();
  }
  
  public toggleShowMore(review: ReviewExtended): void {
    review.expanded = !review.expanded;
    this.cd.detectChanges();
    this.applyEllipsis();
  }
  
  private applyEllipsis(): void {
    this.ellipsisRef.applyEllipsis();
  }
}
